import { useOrganization } from '@/contexts/useOrganization'
import { useRouter } from '@tanstack/react-router'
import { useEffect } from 'react'

/**
 * This component automatically handles organization-related state
 * based on the current route.
 *
 * It follows the principle: clear the organization ID by default,
 * and only keep it when the route explicitly needs it through one of:
 * 1. Having an orgId route parameter
 * 2. Being registered in ORG_RELATED_ROUTES
 * 3. Having an orgId or organizationId query parameter
 */
export function OrganizationRouteHandler() {
  const { clearSelectedOrganizationId, selectedOrganizationId } =
    useOrganization()
  const router = useRouter()

  useEffect(() => {
    // Get the current path and route parameters
    const queryParams = new URLSearchParams(
      Object.entries(router.state.location.search).reduce(
        (acc, [key, value]) => {
          acc[key] = String(value)
          return acc
        },
        {} as Record<string, string>,
      ),
    )

    // Extract all route parameters from all matched routes
    const params = router.state.matches.flatMap((match) =>
      Object.keys(match.params),
    )

    /**
     * Determine if the route needs organization context using multiple checks:
     */

    // 1. Check if any route parameter is 'orgId'
    const hasOrgIdParam = params.includes('orgId')

    // 2. Check for any query parameters that might need org context (optional)
    const hasOrgQueryParam = queryParams.has('orgId')

    // We've removed the redundant hasOrgInPath check since routes with org/orgId in the path
    // would already be captured by the hasOrgIdParam check
    const needsOrgId = hasOrgIdParam || hasOrgQueryParam

    // Only clear the organization ID if:
    // 1. The route doesn't need an organization ID
    // 2. There is currently a selected organization ID (avoid unnecessary state updates)
    if (!needsOrgId && selectedOrganizationId) {
      clearSelectedOrganizationId()
      // console.log(`Organization ID cleared for path: ${currentPath}`); // Uncomment for debugging
    }
  }, [
    router.state.location.search,
    router.state.matches,
    clearSelectedOrganizationId,
    selectedOrganizationId,
  ])

  return null
}
